import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { Search, Package, RefreshCw, ChevronDown, ChevronRight, Users, Send, Loader2, AlertCircle, CheckSquare, ShoppingCart, Star, Mail, Phone, MapPin, FileSpreadsheet } from "lucide-react";
import * as XLSX from 'xlsx';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@mantis-sas/ui";
import { getAllQuotedItems } from "../../../../ApiCalls/mantisFlowsItems";
import { getSuppliers } from "../../../../ApiCalls/mantisFlowsSuppliers";
import { sendRFQToSupplier, createQuotationsForSuppliers } from "../../../../ApiCalls/mantisFlowsManangment";
import '../components/GroupedItemsTable';
import ItemsStats from '../components/ItemsStats';
import './ItemsSection.css';

// Enhanced Supplier Card Component with improved visual design
const EnhancedSupplierCard = ({ supplier, isSelected, onToggle, assignedItemsCount = 0 }) => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`h-3 w-3 ${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <Card 
      className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
        isSelected 
          ? 'ring-2 ring-blue-500 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30 shadow-md' 
          : 'hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }`}
      onClick={() => onToggle(supplier.id)}
    >
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header with avatar and main info */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${
                isSelected 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 dark:from-slate-700 dark:to-slate-600 dark:text-slate-200'
              }`}>
                {supplier.name?.charAt(0)?.toUpperCase() || 'S'}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate" title={supplier.name}>
                  {supplier.name}
                </h3>
                <div className="flex items-center space-x-1 mt-1">
                  <MapPin className="h-3 w-3 text-slate-400" />
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate" title={supplier.country}>
                    {supplier.country || 'No especificado'}
                  </span>
                </div>
                {/* Rating */}
                {supplier.rating > 0 && (
                  <div className="flex items-center space-x-1 mt-1">
                    <div className="flex space-x-0.5">
                      {renderStars(supplier.rating)}
                    </div>
                    <span className="text-xs text-slate-500 ml-1">
                      ({supplier.rating.toFixed(1)})
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Selection indicator and assigned items count */}
            <div className="flex flex-col items-end space-y-1">
              {isSelected && (
                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <CheckSquare className="h-3 w-3 text-white" />
                </div>
              )}
              {assignedItemsCount > 0 && (
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  {assignedItemsCount} items
                </Badge>
              )}
            </div>
          </div>

          {/* Contact information */}
          <div className="space-y-1.5 text-xs">
            {supplier.email && (
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                <Mail className="h-3 w-3 flex-shrink-0" />
                <span className="truncate" title={supplier.email}>{supplier.email}</span>
              </div>
            )}
            {supplier.phone && (
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                <Phone className="h-3 w-3 flex-shrink-0" />
                <span className="truncate" title={supplier.phone}>{supplier.phone}</span>
              </div>
            )}
          </div>

          {/* Additional info */}
          <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-700">
            <div className="flex space-x-2">
              {supplier.is_favorite && (
                <Badge variant="outline" className="text-xs border-yellow-300 text-yellow-600 bg-yellow-50 dark:border-yellow-600 dark:text-yellow-400 dark:bg-yellow-900/20">
                  <Star className="h-2.5 w-2.5 mr-1 fill-current" />
                  Favorito
                </Badge>
              )}
              {supplier.typicalLeadTime && (
                <Badge variant="outline" className="text-xs">
                  {supplier.typicalLeadTime}d
                </Badge>
              )}
            </div>
            {supplier.currency && (
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {supplier.currency}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Grouped Items Table with Selection
const SelectableGroupedItemsTable = ({ 
  data, 
  loading, 
  onRefresh, 
  clientId, 
  selectedItems, 
  onItemToggle,
  selectionMode 
}) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Verificar si todos los grupos están seleccionados
  const areAllGroupsSelected = data.length > 0 && data.every(group => selectedItems.has(group.groupKey));
  
  // Función para seleccionar/deseleccionar todos los grupos
  const handleSelectAll = (checked) => {
    data.forEach(group => {
      onItemToggle(group.groupKey, checked);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        No hay items disponibles
      </div>
    );
  }

  return (
    <div className="overflow-auto max-h-[600px]">
      <Table>
        <TableHeader className="sticky top-0 bg-slate-50 dark:bg-slate-800 z-10">
          <TableRow>
            {selectionMode && (
              <TableHead className="w-12">
                <Checkbox
                  checked={areAllGroupsSelected}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
            )}
            <TableHead>Item</TableHead>
            <TableHead>Código</TableHead>
            <TableHead>Cantidad Total</TableHead>
            <TableHead>Stock</TableHead>
            <TableHead>IPRs</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((group) => (
            <React.Fragment key={group.groupKey}>
              <TableRow className="bg-slate-50/50 dark:bg-slate-800/50 hover:bg-slate-100/50 dark:hover:bg-slate-700/50">
                {selectionMode && (
                  <TableCell>
                    <Checkbox
                      checked={selectedItems.has(group.groupKey)}
                      onCheckedChange={(checked) =>
                        onItemToggle(group.groupKey, checked)
                      }
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleGroup(group.groupKey)}
                      className="h-6 w-6 p-0"
                    >
                      {expandedGroups.has(group.groupKey) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    <div>
                      <div className="font-medium text-sm">
                        {group.name || "Sin nombre"}
                      </div>
                      {group.description && (
                        <div className="text-xs text-slate-500 truncate max-w-48">
                          {group.description}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                    {group.item_code || "N/A"}
                  </code>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-semibold">
                    {group.totalQuantity?.toFixed(2) || "0"}{" "}
                    {group.unit || "units"}
                  </div>
                  <div className="text-xs text-slate-500">
                    Promedio: {group.avgQuantity?.toFixed(2) || "0"}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-semibold">
                    {group.totalStock?.toFixed(2) || "0"}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {group.iprCount} IPRs
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleGroup(group.groupKey)}
                    className="text-xs"
                  >
                    {expandedGroups.has(group.groupKey)
                      ? "Ocultar"
                      : "Ver detalles"}
                  </Button>
                </TableCell>
              </TableRow>
              {expandedGroups.has(group.groupKey) &&
                group.iprs &&
                group.iprs.map((ipr, index) => (
                  <TableRow
                    key={`${group.groupKey}-${index}`}
                    className="bg-blue-50/30 dark:bg-blue-900/10"
                  >
                    {selectionMode && <TableCell></TableCell>}
                    <TableCell className="pl-12">
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        IPR #{ipr.ipr_id}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-slate-500">—</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        {ipr.quantity} {ipr.unit || group.unit}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">{ipr.stock}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-slate-500">
                        {new Date(ipr.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function ItemsSection({ mantisBusiness }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const { toast } = useToast();

  // Estados para suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliersIds, setSelectedSuppliersIds] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [suppliersError, setSuppliersError] = useState(null);
  const [suppliersSearchTerm, setSuppliersSearchTerm] = useState("");
  
  // Estados para selección de items
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  // Estados para asignación de items a suppliers
  const [supplierItemsSelection, setSupplierItemsSelection] = useState({});
  const [isSendingRFQs, setIsSendingRFQs] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Función para agrupar items por ID y nombre
  const groupItemsByItemId = (items) => {
    const grouped = {};
    
    items.forEach(item => {
      // Crear una clave compuesta usando nombre y código del item para agrupar correctamente
      // Esto manejará casos donde items iguales tienen diferentes item_id
      const itemName = (item.name || '').trim().toLowerCase();
      const itemCode = (item.item_code || '').trim().toLowerCase();
      const key = `${itemName}_${itemCode}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          ...item,
          iprs: [],
          totalQuantity: 0,
          totalStock: 0,
          // Usar el primer item_id encontrado como referencia
          groupKey: key
        };
      }
      
      // Agregar información del IPR
      const iprInfo = {
        ipr_id: item.initial_purchase_request_id,
        quantity: item.quantity_to_order,
        stock: item.current_stock,
        created_at: item.created_at,
        unit: item.unit,
        item_id: item.item_id || item.id
      };
      
      grouped[key].iprs.push(iprInfo);
      grouped[key].totalQuantity += parseFloat(item.quantity_to_order) || 0;
      
      // Para el stock, tomar el valor más reciente o el máximo disponible
      // ya que items iguales deberían tener el mismo stock
      const currentStock = parseFloat(item.current_stock) || 0;
      if (currentStock > grouped[key].totalStock) {
        grouped[key].totalStock = currentStock;
      }
    });
    
    // Convertir a array y calcular estadísticas
    return Object.values(grouped).map(group => ({
      ...group,
      iprCount: group.iprs.length,
      avgQuantity: group.totalQuantity / group.iprs.length,
      // Determinar el estado del stock para el grupo
      stock_status: group.totalStock >= group.totalQuantity ? 'sufficient' : 
                   group.totalStock > 0 ? 'partial' : 'insufficient'
    }));
  };

  // Función para cargar los datos de items
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAllQuotedItems(mantisBusiness._id);
      
      if (response.success) {
        const formattedResults = response.data.map((item) => {
          const formattedItem = { ...item };

          // Formatear fecha de creación
          if (item.created_at) {
            formattedItem.created_at = new Date(item.created_at).toLocaleString();
          }

          // Formatear fecha de actualización
          if (item.updated_at) {
            formattedItem.updated_at = new Date(item.updated_at).toLocaleString();
          }

          // Calcular estado del stock
          if (item.current_stock !== null && item.quantity_to_order !== null) {
            if (item.current_stock < item.quantity_to_order) {
              formattedItem.stock_status = "low";
            } else if (item.current_stock >= item.quantity_to_order * 2) {
              formattedItem.stock_status = "high";
            } else {
              formattedItem.stock_status = "normal";
            }
          } else {
            formattedItem.stock_status = "unknown";
          }

          return formattedItem;
        });

        setData(formattedResults);
        
        // Agrupar los datos
        const grouped = groupItemsByItemId(formattedResults);
        setGroupedData(grouped);
        
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los items",
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error("Error al cargar los items:", err);
      setError("Error al cargar los items");
      toast({
        title: "Error",
        description: "Error al cargar los items",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar suppliers
  const loadSuppliers = async () => {
    try {
      console.log('🔄 Cargando suppliers para mantisBusiness._id:', mantisBusiness._id);
      setIsLoadingSuppliers(true);
      setSuppliersError(null);
      
      const response = await getSuppliers(mantisBusiness._id);
      console.log('📋 Respuesta completa de getSuppliers:', response);
      
      if (response.success) {
        console.log('✅ Suppliers cargados exitosamente:', response.data?.length, 'suppliers encontrados');
        console.log('📊 Datos de suppliers:', response.data);
        
        const formattedSuppliers = response.data.map(supplier => ({
          id: supplier.id,
          name: supplier.name,
          country: supplier.country,
          email: supplier.contact_email || supplier.email,
          phone: supplier.phone,
          address: supplier.address,
          currency: supplier.currency,
          rating: supplier.rating_overall || 0,
          rating_service: supplier.rating_service || 0,
          rating_punctuality: supplier.rating_punctuality || 0,
          rating_quality: supplier.rating_quality || 0,
          is_favorite: supplier.is_favorite || false,
          description: supplier.description,
          capabilities: Array.isArray(supplier.capabilities) ? supplier.capabilities : [],
          typicalLeadTime: supplier.typicalleadtime
        }));
        
        console.log('🔄 Suppliers formateados:', formattedSuppliers);
        setSuppliers(formattedSuppliers);
        
        // Toast de éxito
        toast({
          title: "Suppliers cargados",
          description: `Se cargaron ${formattedSuppliers.length} suppliers exitosamente`,
          variant: "default"
        });
      } else {
        console.error('❌ Error en respuesta de suppliers:', response.message || 'Success = false');
        setSuppliersError('No se pudieron cargar los proveedores correctamente');
        
        toast({
          title: "Error al cargar suppliers",
          description: response.message || 'No se pudieron cargar los proveedores correctamente',
          variant: "destructive"
        });
      }
          } catch (error) {
        console.error("💥 Error al cargar suppliers:", error);
        setSuppliersError(error.message || 'Error al cargar los proveedores');
        
        toast({
          title: "Error de conexión",
          description: error.message || 'Error de conexión al cargar los proveedores',
          variant: "destructive"
        });
    } finally {
      setIsLoadingSuppliers(false);
    }
  };

  // Función para togglear selección de items
  const handleItemToggle = (itemId, checked) => {
    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(itemId);
        // Si se selecciona un grupo, también agregamos todos sus items individuales
        const group = groupedData.find(g => g.groupKey === itemId);
        if (group && group.iprs) {
          group.iprs.forEach(ipr => {
            const originalItem = data.find(item => 
              item.item_id === ipr.item_id || 
              item.initial_purchase_request_id === ipr.ipr_id
            );
            if (originalItem) {
              newSelected.add(originalItem.id);
            }
          });
        }
      } else {
        newSelected.delete(itemId);
        // Si se deselecciona un grupo, también quitamos todos sus items individuales
        const group = groupedData.find(g => g.groupKey === itemId);
        if (group && group.iprs) {
          group.iprs.forEach(ipr => {
            const originalItem = data.find(item => 
              item.item_id === ipr.item_id || 
              item.initial_purchase_request_id === ipr.ipr_id
            );
            if (originalItem) {
              newSelected.delete(originalItem.id);
            }
          });
        }
      }
      return newSelected;
    });
  };

  // Función para entrar/salir del modo selección
  const toggleSelectionMode = () => {
    const newMode = !selectionMode;
    setSelectionMode(newMode);
    if (newMode) {
      // Si entramos al modo selección, cargar suppliers
      console.log('🔄 Entrando al modo selección, cargando suppliers...');
      loadSuppliers();
    } else {
      // Si salimos del modo selección, limpiar selecciones
      setSelectedItems(new Set());
      setSelectedSuppliersIds([]);
      setSupplierItemsSelection({});
    }
  };

  // Enviar RFQs a suppliers seleccionados
  const sendRFQsToSuppliers = async () => {
    try {
      setIsSendingRFQs(true);
      
      // Obtener los items seleccionados del data original
      const selectedItemsData = [];
      
      // Recorrer las selecciones y construir la lista de items
      selectedItems.forEach(selectedId => {
        // Verificar si es un grupo seleccionado
        const group = groupedData.find(g => g.groupKey === selectedId);
        if (group && group.iprs) {
          // Si es un grupo, agregar todos los items del grupo
          group.iprs.forEach(ipr => {
            const originalItem = data.find(item => 
              item.item_id === ipr.item_id || 
              item.initial_purchase_request_id === ipr.ipr_id
            );
            if (originalItem && !selectedItemsData.find(existing => existing.id === originalItem.id)) {
              selectedItemsData.push(originalItem);
            }
          });
        } else {
          // Si es un item individual
          const directItem = data.find(item => item.id === selectedId);
          if (directItem && !selectedItemsData.find(existing => existing.id === directItem.id)) {
            selectedItemsData.push(directItem);
          }
        }
      });

      console.log('Items seleccionados para enviar:', selectedItemsData);

      if (selectedItemsData.length === 0) {
        toast({
          title: "Sin items seleccionados",
          description: "Por favor selecciona al menos un item para enviar",
          variant: "destructive"
        });
        return;
      }

      // Construir suppliersWithItems - asignar todos los items seleccionados a todos los suppliers seleccionados
      const suppliersWithItems = selectedSuppliersIds
        .map(supplierId => {
          const supplier = suppliers.find(s => String(s.id) === String(supplierId));
          if (!supplier) return null;
          
          return {
            id: supplier.id,
            name: supplier.name,
            phone: supplier.phone,
            email: supplier.email,
            items: selectedItemsData // Todos los items van a todos los suppliers
          };
        })
        .filter(Boolean);
      
      if (suppliersWithItems.length === 0) {
        toast({
          title: "Sin suppliers seleccionados",
          description: "Por favor selecciona al menos un supplier",
          variant: "destructive"
        });
        return;
      }
      
      console.log('Enviando RFQs a suppliers:', suppliersWithItems);
      
      // Preparar las opciones para el envío
      const options = {
        clientName: mantisBusiness?.name || 'Cliente',
        subject: `RFQ - Request for Quotation`,
        message: '',
        attachments: []
      };
      
      // Como no tenemos un orderId específico en esta vista, usaremos un valor temporal
      const tempOrderId = `items-${Date.now()}`;
      
      // Enviar RFQs
      const response = await sendRFQToSupplier(
        mantisBusiness._id,
        tempOrderId,
        suppliersWithItems,
        options
      );
      
      // Crear cotizaciones para los suppliers seleccionados
      const supplierIds = suppliersWithItems.map(supplier => supplier.id);
      try {
        await createQuotationsForSuppliers(
          mantisBusiness._id,
          tempOrderId,
          supplierIds,
          suppliersWithItems
        );
        toast({
          title: "¡Éxito!",
          description: `Cotizaciones creadas exitosamente para ${supplierIds.length} suppliers`,
          variant: "default"
        });
      } catch (quotationError) {
        console.error('Error creating quotations:', quotationError);
        toast({
          title: "Advertencia",
          description: `RFQs enviados pero error al crear cotizaciones: ${quotationError.message}`,
          variant: "destructive"
        });
      }
      
      toast({
        title: "RFQs enviados",
        description: `RFQs enviados exitosamente a ${suppliersWithItems.length} suppliers con ${selectedItemsData.length} items`,
        variant: "default"
      });
      
      // Limpiar selecciones después del envío exitoso
      setSelectedItems(new Set());
      setSelectedSuppliersIds([]);
      setSupplierItemsSelection({});
      setSelectionMode(false);
      
    } catch (error) {
      console.error('Error al enviar RFQs:', error);
      toast({
        title: "Error",
        description: 'Error al enviar RFQs: ' + (error.message || 'Error desconocido'),
        variant: "destructive"
      });
    } finally {
      setIsSendingRFQs(false);
    }
  };

  useEffect(() => {
    fetchData();
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mantisBusiness._id]);

  // Función para exportar items a Excel
  const exportItemsToExcel = async () => {
    try {
      setIsExporting(true);
      
      // Mostrar toast de inicio
      toast({
        title: "Preparando exportación",
        description: "Generando archivo Excel...",
        variant: "default"
      });
      
      // Preparar datos para exportación
      const dataToExport = [];
      
      // Si hay datos filtrados, usar esos; sino usar todos los datos agrupados
      const dataSource = filteredGroupedData.length > 0 ? filteredGroupedData : groupedData;
      
      // Exportar datos agrupados
      dataSource.forEach((group) => {
        // Agregar fila del grupo principal
        dataToExport.push({
          'Tipo': 'GRUPO',
          'Nombre del Item': group.name || 'Sin nombre',
          'Código': group.item_code || 'N/A',
          'Descripción': group.description || 'Sin descripción',
          'Cantidad Total': group.totalQuantity?.toFixed(2) || '0',
          'Cantidad Promedio': group.avgQuantity?.toFixed(2) || '0',
          'Stock Total': group.totalStock?.toFixed(2) || '0',
          'Estado de Stock': group.stock_status === 'sufficient' ? 'Suficiente' :
                            group.stock_status === 'partial' ? 'Parcial' : 'Insuficiente',
          'Unidad': group.unit || '',
          'Número de IPRs': group.iprCount || 0,
          'IPR ID': '',
          'Fecha de Creación': '',
          'Stock Individual': ''
        });
        
        // Agregar filas de IPRs individuales
        if (group.iprs && group.iprs.length > 0) {
          group.iprs.forEach((ipr) => {
            dataToExport.push({
              'Tipo': 'IPR',
              'Nombre del Item': `  └─ ${group.name || 'Sin nombre'}`,
              'Código': group.item_code || 'N/A',
              'Descripción': group.description || 'Sin descripción',
              'Cantidad Total': '',
              'Cantidad Promedio': '',
              'Stock Total': '',
              'Estado de Stock': '',
              'Unidad': ipr.unit || group.unit || '',
              'Número de IPRs': '',
              'IPR ID': ipr.ipr_id || '',
              'Fecha de Creación': ipr.created_at ? new Date(ipr.created_at).toLocaleDateString('es-CO') : '',
              'Stock Individual': ipr.stock || '0',
              'Cantidad Individual': ipr.quantity || '0'
            });
          });
        }
        
        // Agregar fila separadora
        dataToExport.push({
          'Tipo': '',
          'Nombre del Item': '',
          'Código': '',
          'Descripción': '',
          'Cantidad Total': '',
          'Cantidad Promedio': '',
          'Stock Total': '',
          'Estado de Stock': '',
          'Unidad': '',
          'Número de IPRs': '',
          'IPR ID': '',
          'Fecha de Creación': '',
          'Stock Individual': '',
          'Cantidad Individual': ''
        });
      });
      
      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      
      // Crear hoja principal con datos agrupados
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      
      // Configurar anchos de columnas
      const columnWidths = [
        { wch: 10 }, // Tipo
        { wch: 25 }, // Nombre del Item
        { wch: 15 }, // Código
        { wch: 30 }, // Descripción
        { wch: 15 }, // Cantidad Total
        { wch: 15 }, // Cantidad Promedio
        { wch: 15 }, // Stock Total
        { wch: 15 }, // Estado de Stock
        { wch: 10 }, // Unidad
        { wch: 12 }, // Número de IPRs
        { wch: 15 }, // IPR ID
        { wch: 15 }, // Fecha de Creación
        { wch: 15 }, // Stock Individual
        { wch: 15 }  // Cantidad Individual
      ];
      ws['!cols'] = columnWidths;
      
      // Agregar hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, 'Items Agrupados');
      
      // Crear hoja de resumen
      const summaryData = [
        {
          'Métrica': 'Total de Items Únicos',
          'Valor': dataSource.length
        },
        {
          'Métrica': 'Total de IPRs',
          'Valor': dataSource.reduce((sum, group) => sum + (group.iprCount || 0), 0)
        },
        {
          'Métrica': 'Cantidad Total a Ordenar',
          'Valor': dataSource.reduce((sum, group) => sum + (group.totalQuantity || 0), 0).toFixed(2)
        },
        {
          'Métrica': 'Items con Stock Suficiente',
          'Valor': dataSource.filter(group => group.stock_status === 'sufficient').length
        },
        {
          'Métrica': 'Items con Stock Insuficiente',
          'Valor': dataSource.filter(group => group.stock_status === 'insufficient').length
        },
        {
          'Métrica': 'Items con Stock Parcial',
          'Valor': dataSource.filter(group => group.stock_status === 'partial').length
        },
        {
          'Métrica': 'Fecha de Exportación',
          'Valor': new Date().toLocaleString('es-CO')
        },
        {
          'Métrica': 'Empresa',
          'Valor': mantisBusiness?.name || 'No especificada'
        }
      ];
      
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 25 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumen');
      
      // Generar nombre del archivo
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Items_${mantisBusiness?.name || 'MantisControl'}_${timestamp}.xlsx`;
      
      // Simular un pequeño delay para mostrar el feedback
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Descargar archivo
      XLSX.writeFile(wb, filename);
      
      toast({
        title: "¡Exportación exitosa!",
        description: `Se exportaron ${dataSource.length} items agrupados con ${dataSource.reduce((sum, group) => sum + (group.iprCount || 0), 0)} IPRs a Excel`,
        variant: "default"
      });
      
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      toast({
        title: "Error de exportación",
        description: "No se pudo exportar los datos a Excel. Inténtalo de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Calcular estadísticas de selección
  const totalSelectedItems = Object.values(supplierItemsSelection).reduce((total, items) => {
    return total + (items ? items.size : 0);
  }, 0);
  
  const hasSelectedItems = selectedItems.size > 0;
  const hasSelectedSuppliers = selectedSuppliersIds.length > 0;

  // Filtrar datos agrupados basado en el término de búsqueda
  const filteredGroupedData = groupedData.filter(group => 
    group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.item_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.unit?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.iprs.some(ipr => ipr.ipr_id?.toString().includes(searchTerm))
  );

  // Filtrar y ordenar suppliers alfabéticamente
  const filteredAndSortedSuppliers = suppliers
    .filter(supplier => 
      supplier.name?.toLowerCase().includes(suppliersSearchTerm.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(suppliersSearchTerm.toLowerCase()) ||
      supplier.country?.toLowerCase().includes(suppliersSearchTerm.toLowerCase()) ||
      supplier.phone?.includes(suppliersSearchTerm)
    )
    .sort((a, b) => {
      // Ordenar favoritos primero, luego alfabéticamente
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;
      return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'accent' });
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6 items-section"
    >
      {/* Estadísticas */}
      <ItemsStats data={data} mantisBusiness={mantisBusiness} />

      {/* Layout principal: dinámico basado en modo selección */}
      <div className={`flex gap-6 ${selectionMode ? '' : ''}`}>
        {/* Sección izquierda - Items */}
        <div className={`${selectionMode ? 'w-[60%]' : 'w-full'} space-y-4 transition-all duration-300`}>
          {/* Información de agrupación */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                  <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100">Items Disponibles</h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {groupedData.length} items únicos encontrados de {data.length} solicitudes totales
                  </p>
                </div>
              </div>
              {selectionMode && hasSelectedItems && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  {selectedItems.size} seleccionados
                </Badge>
              )}
            </div>
          </div>

          {/* Table Container with integrated search */}
          <div className="bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-200/80 dark:border-slate-700/80 overflow-hidden items-section-container">
            {/* Search and Actions Header */}
            <div className="px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Input
                      placeholder="Buscar items por nombre, código, descripción o IPR..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 w-80 rounded border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                    />
                  </div>
                  <Button
                    variant={selectionMode ? "default" : "outline"}
                    size="sm"
                    onClick={toggleSelectionMode}
                    className="h-10 px-4"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {selectionMode ? 'Salir de Selección' : 'Seleccionar Items'}
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportItemsToExcel}
                    disabled={loading || isExporting || filteredGroupedData.length === 0}
                    className="h-10 px-4 export-button"
                  >
                    {isExporting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                    )}
                    {isExporting ? 'Exportando...' : 'Exportar Excel'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchData}
                    disabled={loading}
                    className="h-10 px-4 border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700/50"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Selectable Grouped Table */}
            <div className="items-table-scroll">
              <SelectableGroupedItemsTable
                data={filteredGroupedData}
                loading={loading}
                onRefresh={fetchData}
                clientId={mantisBusiness._id}
                selectedItems={selectedItems}
                onItemToggle={handleItemToggle}
                selectionMode={selectionMode}
              />
            </div>
          </div>
        </div>

        {/* Sección derecha - Suppliers y Pedidos - Solo aparece cuando está en modo selección */}
        {selectionMode && (
          <motion.div 
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.3 }}
            className="w-[38%] space-y-4"
          >
            {/* RFQ Actions Bar */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded border border-green-200 dark:border-green-800 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-800 rounded flex items-center justify-center">
                    <Send className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-900 dark:text-green-100 text-sm">Crear Pedidos</h3>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      Selecciona suppliers para enviar RFQs
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="text-center">
                    <div className="text-sm font-bold text-green-600 dark:text-green-400">{selectedSuppliersIds.length}</div>
                    <div className="text-xs text-green-700 dark:text-green-300">Suppliers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{selectedItems.size}</div>
                    <div className="text-xs text-blue-700 dark:text-blue-300">Items</div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendRFQsToSuppliers}
                    disabled={isSendingRFQs || !hasSelectedSuppliers || !hasSelectedItems}
                    className="border-green-200 text-green-600 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/20 text-xs"
                  >
                    {isSendingRFQs ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3 mr-1" />
                    )}
                    Enviar RFQs
                  </Button>
                </div>
              </div>
            </div>

            {/* Suppliers Selection */}
            <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm suppliers-section-container">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-slate-100 text-sm flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Seleccionar Proveedores
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {filteredAndSortedSuppliers.length} de {suppliers.length} proveedores
                      {suppliersSearchTerm && ` (filtrados)`}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadSuppliers}
                    disabled={isLoadingSuppliers}
                    className="h-8 px-3 border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700/50"
                  >
                    <RefreshCw className={`h-3 w-3 ${isLoadingSuppliers ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                {/* Search bar for suppliers */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
                  <Input
                    placeholder="Buscar proveedores por nombre, email, país..."
                    value={suppliersSearchTerm}
                    onChange={(e) => setSuppliersSearchTerm(e.target.value)}
                    className="pl-9 h-8 text-xs border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800"
                  />
                  {suppliersSearchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSuppliersSearchTerm("")}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      ×
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="suppliers-list-scroll p-3">
                {isLoadingSuppliers ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500 mx-auto mb-2" />
                    <span className="text-xs text-slate-500 dark:text-slate-400">Cargando proveedores...</span>
                  </div>
                ) : suppliersError ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                    <span className="text-xs text-red-600 dark:text-red-400">{suppliersError}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadSuppliers}
                      className="mt-2 text-xs"
                    >
                      Reintentar
                    </Button>
                  </div>
                ) : filteredAndSortedSuppliers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-xs">
                      {suppliersSearchTerm 
                        ? `No se encontraron proveedores que coincidan con "${suppliersSearchTerm}"`
                        : 'No hay proveedores disponibles'
                      }
                    </p>
                    {suppliersSearchTerm && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSuppliersSearchTerm("")}
                        className="mt-2 text-xs"
                      >
                        Limpiar búsqueda
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Quick select all button */}
                    {filteredAndSortedSuppliers.length > 1 && (
                      <div className="pb-2 border-b border-slate-100 dark:border-slate-700">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const allFilteredIds = filteredAndSortedSuppliers.map(s => s.id);
                            const allSelected = allFilteredIds.every(id => selectedSuppliersIds.includes(id));
                            
                            if (allSelected) {
                              // Deseleccionar todos los filtrados
                              setSelectedSuppliersIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
                            } else {
                              // Seleccionar todos los filtrados
                              setSelectedSuppliersIds(prev => {
                                const newSelection = [...prev];
                                allFilteredIds.forEach(id => {
                                  if (!newSelection.includes(id)) {
                                    newSelection.push(id);
                                  }
                                });
                                return newSelection;
                              });
                            }
                          }}
                          className="w-full h-8 text-xs"
                        >
                          {filteredAndSortedSuppliers.every(s => selectedSuppliersIds.includes(s.id)) 
                            ? 'Deseleccionar todos' 
                            : 'Seleccionar todos'
                          }
                        </Button>
                      </div>
                    )}
                    
                    {filteredAndSortedSuppliers.map((supplier) => (
                      <EnhancedSupplierCard
                        key={supplier.id}
                        supplier={supplier}
                        isSelected={selectedSuppliersIds.includes(supplier.id)}
                        onToggle={(supplierId) => {
                          // Toggle de supplier en el array de seleccionados
                          const newSelection = selectedSuppliersIds.includes(supplierId)
                            ? selectedSuppliersIds.filter(id => id !== supplierId)
                            : [...selectedSuppliersIds, supplierId];
                          
                          setSelectedSuppliersIds(newSelection);
                        }}
                        assignedItemsCount={selectedItems.size}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
} 